'use client';

import BuyComponent from 'components/blood-vision/buy/BloodVisionPage';
import { JsonLd } from 'components/shared';
import { HowItWorks } from 'components/blood-vision/buy/HowItWorks';
import { SecondaryNavbar_BloodVision } from 'components/blood-vision/buy/SecondaryNav';
import { FAQSection } from 'components/shared/FAQSection';
import { OffersModalNewProvider } from 'components/shared/OffersModal';
import { LabTestAccordionSection } from 'components/blood-vision/buy/LabTestAccordionSection';
import BloodVisionBottomBar from 'components/blood-vision/buy/BloodVisionBottomBar';
import {
  BloodVisionProvider,
  useBloodVision,
} from 'components/blood-vision/BloodVisionContext';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import VideoHero from 'components/blood-vision/buy/VideoHero';
import { ZipCodeProvider } from 'components/blood-vision/buy/ZipCodeContext';
import { BloodVisionFaqs } from 'components/blood-vision/buy/FaqData';
import { BentoSection } from 'components/blood-vision/BentoSection';
import ComparePlans from 'components/blood-vision/buy/ComparePlans';
import { ProductWizard } from 'components/shared/ProductWizard';
import {
  PrimaryOffers,
  PrimaryOffersTransitionTile,
} from 'components/ring-air/ProductSelectionSection/PrimaryOffers';
import { OffersDataType } from 'components/ring-air/OffersCarousel';
import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { getBloodVisionPricingSchemas } from 'lib/seo/meta/pricing/bloodVision';
import { Currency } from 'utils/Currency';
import dynamic from 'next/dynamic';
import IndiaLayout from 'components/blood-vision/buy/india/IndiaLayout';
import styles from './buy-client.module.css';

// Dynamic import for India page (client-side only due to localStorage usage)
const IndiaBloodVisionPage = dynamic(
  () => import('components/blood-vision/buy/india/IndiaBloodVisionPage'),
  {
    ssr: false,
    // Reserve a viewport so the footer doesn't flash before the chunk mounts.
    loading: () => <div style={{ minHeight: '100vh', background: '#ffffff' }} />,
  },
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BloodVisionBuyPageProps = {
  region: string;
  currency: string;
  isAvailable: boolean;
};

// ---------------------------------------------------------------------------
// Region-specific page variants
// ---------------------------------------------------------------------------

/**
 * US-specific Blood Vision buy page — ZIP code, modality, Stripe/HSA checkout
 */
/**
 * Wrapper that reads uiComponents from context to filter HSA/FSA tile
 * from PrimaryOffers when offer_section is false.
 */
// Both the desktop (PrimaryOffers) and mobile (PrimaryOffersTransitionTile)
// variants are rendered unconditionally and toggled via CSS at the same 992px
// breakpoint the gate used. This keeps the SSR markup correct without a JS width
// gate (useWindowSize returns 0 on the server + first paint), which previously
// rendered the mobile variant then swapped to desktop after hydration and shifted
// the hero/everything below. The wrapper reserves a stable min-height matching the
// taller-rendered variant at each breakpoint so no shift remains either way.
const PrimaryOffersSection = ({
  offersData,
}: {
  offersData: OffersDataType[];
}) => {
  const { uiComponents } = useBloodVision();

  const filteredOffers = uiComponents.offer_section
    ? offersData
    : offersData.filter(
        (offer) => !offer.image?.some((img) => img.includes('hsa-fsa')),
      );

  return (
    <div className={styles.primaryOffersWrapper}>
      <div className={styles.desktopPrimaryOffers}>
        <PrimaryOffers data={filteredOffers} />
      </div>
      <div className={styles.mobilePrimaryOffers}>
        <PrimaryOffersTransitionTile data={filteredOffers} />
      </div>
    </div>
  );
};

const USBloodVision = ({
  region,
  offersData = [
    {
      title: (
        <>
          Instant payment with <b>Stripe</b>
        </>
      ),
      image: ['web_v2/icons/stripe.png'],
    },
    {
      title: (
        <>
          <b>Save more</b> with your pre-tax dollars
        </>
      ),
      image: ['web_v2/icons/hsa-fsa/hsa-fsa-black-outline.svg'],
    },
    {
      title: <>Crypto payments with USDC</>,
      image: ['web_v2/icons/primary_offers/usdc-2.png'],
    },
    {
      title: (
        <>
          As low as <b>0% APR</b> financing with Klarna
        </>
      ),
      image: ['web_v2/icons/primary_offers/klara.png'],
    },
  ],
  isAvailable,
  currency: currencyProp,
}: BloodVisionBuyPageProps & { offersData?: OffersDataType[] }) => {
  const country = region;
  useGlobalUI({ headerCollaspable: true });
  const bloodVisionSchemas = getBloodVisionPricingSchemas({
    country,
    currency: (currencyProp as Currency) || Currency.usd,
    isAvailable,
  });
  return (
    <>
      <JsonLd data={bloodVisionSchemas} />
      <OffersModalNewProvider>
        <div
          style={{
            paddingTop: isMarketingBannerActive() ? '80px' : '48px',
          }}
        />
        <BloodVisionProvider country={country || 'us'}>
          <ZipCodeProvider>
            {offersData && <PrimaryOffersSection offersData={offersData} />}
            <BuyComponent country={country} isAvailable={isAvailable} />
            <ComparePlans />
            <SecondaryNavbar_BloodVision />
            <HowItWorks htmlId="how-it-works" />
            <BentoSection />
            <LabTestAccordionSection htmlId="what-is-included" />
            <VideoHero />
            <ProductWizard title="Still unsure if Blood Vision is for you?" />
            <FAQSection
              faqData={BloodVisionFaqs}
              htmlId="common-questions"
              bgColor="white"
            />
            <BloodVisionBottomBar />
          </ZipCodeProvider>
        </BloodVisionProvider>
      </OffersModalNewProvider>
    </>
  );
};

/**
 * India-specific Blood Vision buy page — pincode, plan cards, slot booking
 */
const INBloodVision = () => {
  useGlobalUI({ headerCollaspable: true });

  return (
    <OffersModalNewProvider>
      <IndiaLayout>
        <IndiaBloodVisionPage />
      </IndiaLayout>
    </OffersModalNewProvider>
  );
};

// ---------------------------------------------------------------------------
// Page component — routes to the correct variant
// ---------------------------------------------------------------------------

export default function BloodVisionBuy(props: BloodVisionBuyPageProps) {
  const { region } = props;
  const regionUpper = region.toUpperCase();

  // India
  if (regionUpper === 'IN') {
    return <INBloodVision />;
  }

  // US (default)
  return <USBloodVision {...props} />;
}
